import React, { createContext, useCallback, useContext, useEffect, useState, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { apiFetch } from '../api/client';
// Le retour d'onglet est une action cliente : elle doit exister pour pouvoir être une cause.
import { track } from '../insights/beacon';
import { navigateNotificationRoute, setScenarioLookup } from '../utils/notificationRoute';
import { getScenarioForMission } from '../data/scenarios';
import { navigateFromAnywhere } from '../navigation/navRef';

// Notification telle que renvoyée par le backend (GET /me/notifications).
export interface ApiNotification {
  id: number;
  type: string;
  title: string;
  message: string;
  route?: string | null;   // deep-link au clic (ex. /company/12?tab=members)
  unread: boolean;
  createdAt: string | null;
  senderName?: string | null;    // personnage émetteur (push narratif)
  senderAvatar?: string | null;  // URL avatar (blob)
  senderColor?: string | null;
  /** CCT-124 — mission d'origine (dérivée de la route côté serveur) : la carte l'affiche en tag. */
  missionId?: number | null;
  missionTitle?: string | null;
}

interface NotificationsContextType {
  items: ApiNotification[];
  unread: number;
  loading: boolean;
  error: boolean;
  refresh: () => Promise<void>;
  markRead: (id: number) => Promise<void>;
  markAllRead: () => Promise<void>;
  /** Dernière notification ARRIVÉE pendant la session → carte in-app (CCT-97). */
  toast: ApiNotification | null;
  dismissToast: () => void;
}

const NotificationsContext = createContext<NotificationsContextType | undefined>(undefined);

export function NotificationsProvider({ children }: { children: ReactNode }) {
  const { user, getAccessToken } = useAuth();
  const [items, setItems] = useState<ApiNotification[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [toast, setToast] = useState<ApiNotification | null>(null);
  // Plus grand id DÉJÀ vu : sert à détecter une ARRIVÉE (et non le 1er chargement, qui
  // afficherait une carte pour un historique entier).
  const seenMaxId = React.useRef<number | null>(null);
  // Garde de RÉ-ENTRANCE (CCT-177) : le refresh est déclenché par plusieurs sources (interval,
  // focus, service-worker, montage). Sans garde, un poll lent (cold start) était re-tiré avant sa
  // fin → requêtes empilées. Ici, un refresh déjà en vol court-circuite les suivants.
  const refreshingRef = React.useRef(false);

  // ORIGINE de l'appel (CCT-259). Le poll supprimé (CCT-233), `/me/notifications` est
  // désormais déclenché par des causes RÉELLES : montage de l'écran, retour d'onglet, clic sur
  // « rafraîchir ». Une seule origine reste sans cause cliente — le réveil poussé par le
  // service worker à l'arrivée d'un push. Le marquer indistinctement « sans cause », comme le
  // faisait `background: true`, revenait à effacer une causalité qui existe.
  const refresh = useCallback(async (origine: 'utilisateur' | 'push' = 'utilisateur') => {
    const token = getAccessToken();
    if (!user || !token) { setItems([]); setError(false); setLoading(false); return; }
    if (refreshingRef.current) return; // un poll est déjà en cours : on ne l'empile pas
    refreshingRef.current = true;
    setLoading(true);
    try {
      // background:true → ce poll de fond n'alimente PAS l'indicateur de chargement global
      // (sinon un poll lent/chevauché épinglait le spinner, CCT-177). Le centre garde son
      // propre état `loading`/`error` ci-dessous.
      const data = await apiFetch<{ notifications: ApiNotification[] }>(
        '/me/notifications',
        // `background` reste : cet appel ne doit pas épingler l'indicateur global (CCT-177).
        // `sansCause` ne vaut que pour le réveil POUSSÉ, qui n'a effectivement pas d'auteur.
        { token, auth: true, background: true, withoutCause: origine === 'push' });
      const list = data?.notifications ?? [];
      setItems(list);
      // Carte in-app à l'arrivée d'une notification (CCT-97) : on ne la montre QU'APRÈS le
      // premier chargement, et uniquement pour un id strictement plus récent.
      const maxId = list.reduce((m, n) => (n.id > m ? n.id : m), 0);
      if (seenMaxId.current != null && maxId > seenMaxId.current) {
        const fresh = list.find((n) => n.id === maxId) || null;
        if (fresh && fresh.unread) setToast(fresh);
      }
      seenMaxId.current = maxId;
      setError(false);
    } catch (_) {
      setError(true);
    } finally {
      setLoading(false);
      refreshingRef.current = false;
    }
  }, [user, getAccessToken]);

  useEffect(() => { void refresh(); }, [refresh]);

  // CCT-133 — branche le STORE de scénarios sur le resolver de notifications : il décide de la
  // cible à partir de ce qui existe dans la mission (élément ? chapitre ?), pas de la syntaxe de
  // la route. L'injection garde le resolver PUR, donc testable sans store ni navigateur.
  useEffect(() => {
    setScenarioLookup((missionId: number) => (getScenarioForMission(missionId) as any) ?? null);
  }, []);

  // Rafraîchissement du centre + badge, UNIQUEMENT SUR ÉVÉNEMENT (CCT-233) :
  //  - message du service worker à l'arrivée d'un push (web) → refresh immédiat ;
  //  - retour de focus sur l'onglet (web).
  //
  // LE POLL A ÉTÉ SUPPRIMÉ. Mesuré sur 20 h de logs du backend dev : `/me/notifications`
  // représentait 639 appels (529 web + 110 iOS), ~32/heure — DE LOIN le premier réveilleur
  // du container app. Chaque appel rallume une réplique pour plusieurs minutes : le backend
  // passait 53 % du temps allumé (12,69 réplique-heures sur 24) alors que `minReplicas` est
  // bien à 0. Un fond d'écran ouvert suffisait à empêcher la mise en veille, la nuit comprise.
  //
  // Ce qui reste couvre les cas réels sans jamais réveiller le serveur « pour voir » :
  // l'arrivée d'un push est POUSSÉE par le service worker, le retour sur l'onglet est une
  // action de l'utilisateur, et le centre de notifications a désormais un bouton de
  // rafraîchissement manuel (NotificationsScreen).
  useEffect(() => {
    const nav = typeof navigator !== 'undefined' ? navigator : undefined;
    const onSwMsg = (ev: MessageEvent) => {
      const d: any = ev.data || {};
      if (d.type === 'cc-push' || d.type === 'cc-push-open') void refresh('push');
      // CLIC sur la notification NAVIGATEUR : le service worker poste la route — sans cette
      // navigation, le clic se contentait de rafraîchir le badge (« aucun effet »). CCT-97.
      if (d.type === 'cc-push-open' && d.route) {
        navigateNotificationRoute({ navigate: (n: any, p: any) => navigateFromAnywhere(n, p) }, d.route);
      }
    };
    if (nav?.serviceWorker) nav.serviceWorker.addEventListener('message', onSwMsg);

    const win: any = typeof window !== 'undefined' ? window : undefined;
    // Revenir sur l'onglet EST une action de l'utilisateur : on l'enregistre pour que l'appel
    // qui suit ait une cause à désigner, au lieu de flotter en racine.
    const onFocus = () => { track({ type: 'focus', path: '/', action: 'return' }); void refresh(); };
    if (win && typeof win.addEventListener === 'function') win.addEventListener('focus', onFocus);

    // (Ici se trouvait le poll de 45 s replanifié après complétion — CCT-177. Retiré par
    // CCT-233 : cf. le commentaire au-dessus de ce useEffect. La garde de ré-entrance de
    // `refresh` reste utile, les sources événementielles pouvant se déclencher ensemble.)

    return () => {
      if (nav?.serviceWorker) nav.serviceWorker.removeEventListener('message', onSwMsg);
      if (win && typeof win.removeEventListener === 'function') win.removeEventListener('focus', onFocus);
    };
  }, [refresh]);

  const markRead = useCallback(async (id: number) => {
    setItems(prev => prev.map(n => n.id === id ? { ...n, unread: false } : n));
    const token = getAccessToken();
    if (!token) return;
    try { await apiFetch(`/me/notifications/${id}/read`, { method: 'POST', token, auth: true }); } catch (_) {}
  }, [getAccessToken]);

  const markAllRead = useCallback(async () => {
    setItems(prev => prev.map(n => ({ ...n, unread: false })));
    const token = getAccessToken();
    if (!token) return;
    try { await apiFetch('/me/notifications/read-all', { method: 'POST', token, auth: true }); } catch (_) {}
  }, [getAccessToken]);

  const unread = items.reduce((n, it) => n + (it.unread ? 1 : 0), 0);

  return (
    <NotificationsContext.Provider value={{ items, unread, loading, error, refresh, markRead, markAllRead,
      toast, dismissToast: () => setToast(null) }}>
      {children}
    </NotificationsContext.Provider>
  );
}

export function useNotifications() {
  const ctx = useContext(NotificationsContext);
  if (!ctx) throw new Error('useNotifications must be used within a NotificationsProvider');
  return ctx;
}
